/**
 * Backlinks — shows incoming references to an entity.
 *
 * Renders nothing when there are no backlinks. Groups results by source type,
 * and each row navigates to the source entity via the existing entityUrls helper.
 */
import { useEffect, useState } from 'react';
import { Link2 } from 'lucide-react';
import { PreviewLink } from './PreviewLink';
import type { EntityType } from './EntityChip';
import { apiGet } from '../lib/api';
import { ENTITY_ICONS, ENTITY_LABELS } from '../lib/entityMeta';
import { getEntityUrl } from '../lib/entityUrls';

export interface BacklinkRow {
  link_id: string;
  source_type: string;
  source_id: string;
  source_title: string;
  relationship: string;
  metadata: string | null;
  created_at: string;
  source_metadata?: { series_id?: string };
}

export interface BacklinksResponse {
  results: BacklinkRow[];
  total: number;
}

interface Props {
  entityType: string;
  entityId: string;
  className?: string;
}

const RELATIONSHIP_LABELS: Record<string, string> = {
  tagged: 'tagged',
  scoped: 'scoped to',
  assigned_to: 'assigned to',
  waiting_on: 'waiting on',
  follow_up_with: 'follow up with',
  follow_up_from: 'follow up from',
  attendee: 'attendee of',
  source_meeting: 'from meeting',
  mentioned_in: 'mentions',
  related: 'related',
};

export function Backlinks({ entityType, entityId, className = '' }: Props) {
  const [data, setData] = useState<BacklinksResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiGet<BacklinksResponse>(`/entity-links/backlinks?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`)
      .then(res => { if (!cancelled) setData(res); })
      .catch(err => { if (!cancelled) setError(err.message || 'Failed to load backlinks'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [entityType, entityId]);

  // Render nothing while loading initial data — avoids layout flash
  if (loading) return null;

  if (error) {
    return (
      <div className={`text-xs text-text-muted ${className}`} role="status">
        Couldn't load backlinks
      </div>
    );
  }

  if (!data || !Array.isArray(data.results) || data.results.length === 0) return null;

  // Group by source_type, preserving the API's created_at desc order within each group
  const groups = new Map<string, BacklinkRow[]>();
  for (const row of data.results) {
    if (!groups.has(row.source_type)) groups.set(row.source_type, []);
    groups.get(row.source_type)!.push(row);
  }

  return (
    <section className={`${className}`} aria-label="Backlinks">
      <header className="flex items-center gap-2 text-xs uppercase tracking-wider text-text-muted mb-2">
        <Link2 size={12} />
        <span>Linked from</span>
        <span className="text-text-muted">·</span>
        <span>{data.total}</span>
      </header>

      <div className="space-y-3">
        {Array.from(groups.entries()).map(([sourceType, rows]) => {
          const Icon = ENTITY_ICONS[sourceType];
          const groupLabel = ENTITY_LABELS[sourceType] ?? sourceType;
          return (
            <div key={sourceType}>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-text-secondary mb-1">
                {Icon && <Icon size={12} />}
                <span>{groupLabel}{rows.length > 1 ? 's' : ''}</span>
                <span className="text-text-muted">({rows.length})</span>
              </div>
              <ul className="space-y-1">
                {rows.map(row => (
                  <BacklinkItem key={row.link_id} row={row} />
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function BacklinkItem({ row }: { row: BacklinkRow }) {
  const url = getEntityUrl({
    entity_type: row.source_type,
    entity_id: row.source_id,
    name: row.source_title,
    metadata: row.source_metadata as Record<string, string> | undefined,
  });
  const relLabel = RELATIONSHIP_LABELS[row.relationship] ?? row.relationship;

  return (
    <li>
      <PreviewLink
        to={url}
        type={row.source_type as EntityType}
        id={row.source_id}
        className="flex items-start gap-2 px-2 py-1.5 rounded-md hover:bg-bg-hover transition-colors group"
      >
        <span className="flex-1 min-w-0 truncate text-sm text-text-primary group-hover:text-accent">
          {row.source_title}
        </span>
        <span className="flex-shrink-0 text-[10px] uppercase tracking-wider text-text-muted bg-bg-input px-1.5 py-0.5 rounded">
          {relLabel}
        </span>
      </PreviewLink>
    </li>
  );
}
